import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCartIcon, MenuIcon, CloseIcon } from '../Icons/CustomIcons';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import useAnalyticsAccess from '../../hooks/useAnalyticsAccess';
import Cart from '../Cart/Cart';

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--header-height);
  z-index: 1000;
  display: flex;
  align-items: center;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-subtle);
`;

const BarInner = styled.div`
  width: 100%;
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  font-family: var(--font-mono);
  font-size: 1.0625rem;
  font-weight: 500;
  color: var(--text-primary);
  flex-shrink: 0;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    background: var(--brand);
  }
`;

const NavDesktop = styled.nav`
  display: none;
  align-items: center;
  gap: 0.25rem;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.9375rem;
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  padding: 0.75rem 0.875rem;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--text-primary);
  }
`;

const LockedNavItem = styled.span`
  font-size: 0.9375rem;
  color: var(--text-tertiary);
  padding: 0.75rem 0.875rem;
  cursor: not-allowed;
  user-select: none;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-md);
  background: transparent;
  border: 1px solid var(--border-accent);
  color: var(--text-primary);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-tertiary);
  }
`;

const CartButton = styled(IconButton)``;

const CartCount = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 4px;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.1rem;
  text-align: center;
  color: var(--brand-ink);
  background: var(--brand);
  border-radius: var(--radius-full);
`;

const AuthCluster = styled.div`
  display: none;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.25rem;
  padding-left: 0.75rem;
  border-left: 1px solid var(--border-subtle);

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const UserEmail = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-tertiary);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GhostBtn = styled.button`
  font-size: 0.8125rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  background: transparent;
  border: 1px solid var(--border-accent);
  color: var(--text-secondary);
  transition: color var(--transition-fast), border-color var(--transition-fast);

  &:hover {
    color: var(--error);
    border-color: var(--error);
  }
`;

const CtaLink = styled(Link)`
  display: none;
  margin-left: 0.5rem;
  padding: 0.6875rem 1.125rem;
  font-size: 0.9375rem;
  font-weight: 600;
  background: var(--brand);
  color: var(--brand-ink);
  border-radius: var(--radius-md);
  white-space: nowrap;

  &:hover {
    background: #ff8f4f;
  }

  @media (min-width: 1200px) {
    display: inline-flex;
  }
`;

const MenuToggle = styled(IconButton)`
  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileDrawer = styled(motion.div)`
  position: fixed;
  top: var(--header-height);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: rgba(4, 5, 6, 0.72);
`;

const MobilePanel = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  width: min(100%, 320px);
  height: 100%;
  background: var(--surface-1);
  border-left: 1px solid var(--border-subtle);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileLink = styled(Link)`
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-secondary);
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  transition: background var(--transition-fast), color var(--transition-fast);

  &:hover {
    color: var(--text-primary);
    background: var(--surface-3);
  }
`;

const LINKS = [
  { to: '/portfolio', label: 'Work' },
  { to: '/prices', label: 'Services' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/shop', label: 'Shop' },
];

const Header = () => {
  const { cart, isCartOpen, setIsCartOpen } = useCart();
  const { user, logout, loading: authLoading } = useAuth();
  const analyticsAllowed = useAnalyticsAccess(user);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const isAuthenticated = !!user?.email;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    window.location.reload();
  };

  return (
    <>
      <Bar>
        <BarInner>
          <Logo to="/">skeered</Logo>

          <NavDesktop aria-label="Main">
            {LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                $active={location.pathname === to}
                aria-current={location.pathname === to ? 'page' : undefined}
              >
                {label}
              </NavLink>
            ))}
            {isAuthenticated && (
              <NavLink to="/dashboard" $active={location.pathname === '/dashboard'}>
                Dashboard
              </NavLink>
            )}
            {!authLoading && isAuthenticated && (
              analyticsAllowed ? (
                <NavLink to="/dashboard/analytics" $active={location.pathname === '/dashboard/analytics'}>
                  Analytics
                </NavLink>
              ) : (
                <LockedNavItem title="Analytics access required">
                  Analytics
                </LockedNavItem>
              )
            )}
          </NavDesktop>

          <Actions>
            <CartButton type="button" onClick={() => setIsCartOpen(true)} aria-label="Open cart">
              <ShoppingCartIcon size={20} />
              {cart.length > 0 && <CartCount>{cart.length}</CartCount>}
            </CartButton>

            <AuthCluster>
              {isAuthenticated ? (
                <>
                  <UserEmail title={user?.email}>{user?.email}</UserEmail>
                  <GhostBtn type="button" onClick={handleLogout}>
                    Log out
                  </GhostBtn>
                </>
              ) : (
                <>
                  <NavLink to="/login">Log in</NavLink>
                  <NavLink to="/register">Sign up</NavLink>
                </>
              )}
            </AuthCluster>

            <CtaLink to="/contact">Start a project</CtaLink>

            <MenuToggle
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <CloseIcon size={22} /> : <MenuIcon size={22} />}
            </MenuToggle>
          </Actions>
        </BarInner>
      </Bar>

      <AnimatePresence>
        {menuOpen && (
          <MobileDrawer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMenuOpen(false)}
          >
            <MobilePanel
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              {LINKS.map(({ to, label }) => (
                <MobileLink key={to} to={to} onClick={() => setMenuOpen(false)}>
                  {label}
                </MobileLink>
              ))}
              {isAuthenticated && (
                <MobileLink to="/dashboard" onClick={() => setMenuOpen(false)}>
                  Dashboard
                </MobileLink>
              )}
              {!authLoading && isAuthenticated && (
                analyticsAllowed ? (
                  <MobileLink to="/dashboard/analytics" onClick={() => setMenuOpen(false)}>
                    Analytics
                  </MobileLink>
                ) : (
                  <div style={{ opacity: 0.72, cursor: 'not-allowed', padding: '0.85rem 1rem', color: 'var(--text-tertiary)', borderRadius: 'var(--radius-lg)' }}>
                    Analytics
                  </div>
                )
              )}
              {!isAuthenticated && (
                <>
                  <MobileLink to="/login" onClick={() => setMenuOpen(false)}>
                    Log in
                  </MobileLink>
                  <MobileLink to="/register" onClick={() => setMenuOpen(false)}>
                    Sign up
                  </MobileLink>
                </>
              )}
            </MobilePanel>
          </MobileDrawer>
        )}
      </AnimatePresence>

      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default Header;
